import { HD_STATUS } from './status';

export interface ActivityEntry {
  action: string;
  userName?: string | null;
  actorType?: string;
  payload?: unknown;
}

const TEMPLATE_NAME: Record<string, string> = {
  ticket_received: 'Přijetí dotazu',
  status_changed: 'Změna stavu',
  assignee_changed: 'Přiřazení ticketu',
  reply_greeting: 'Oslovení v odpovědi',
};

function asRecord(payload: unknown): Record<string, unknown> {
  if (payload && typeof payload === 'object') return payload as Record<string, unknown>;
  if (typeof payload === 'string') {
    try {
      const parsed = JSON.parse(payload) as unknown;
      return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : {};
    } catch {
      return {};
    }
  }
  return {};
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function statusLabel(value: unknown): string | null {
  const s = str(value);
  return s ? HD_STATUS[s]?.label ?? s : null;
}

function templateLabel(value: unknown): string {
  const code = str(value);
  if (!code) return 'e-mail';
  return `„${TEMPLATE_NAME[code] ?? code}“`;
}

function by(entry: ActivityEntry): string {
  const name = str(entry.userName);
  if (name) return ` — ${name}`;
  return entry.actorType === 'system' ? ' — automaticky' : '';
}

export function activityText(entry: ActivityEntry): string {
  const p = asRecord(entry.payload);

  switch (entry.action) {
    case 'gmail_message_ingested': {
      const from = str(p.from);
      return from ? `Přijetí e-mailu od ${from}` : 'Přijetí e-mailu';
    }
    case 'auto_reply_sent':
      return `Odeslán automatický e-mail ${templateLabel(p.template)}`;
    case 'auto_notification_sent':
      return `Odeslán e-mail klientovi ${templateLabel(p.template)}`;
    case 'assignee_email_sent': {
      const to = str(p.to);
      return to ? `Odeslána interní notifikace na ${to}` : 'Odeslána interní notifikace';
    }
    case 'reply_created':
      return `Odeslána odpověď zákazníkovi${by(entry)}`;
    case 'note_added':
      return `Přidána interní poznámka${by(entry)}`;
    case 'status_changed': {
      const to = statusLabel(p.to);
      const from = statusLabel(p.from);
      const auto = str(p.reason) === 'auto_close';
      if (auto) return 'Ticket automaticky uzavřen po nečinnosti';
      if (to && from) return `Změna stavu z „${from}“ na „${to}“${by(entry)}`;
      if (to) return `Změna stavu na „${to}“${by(entry)}`;
      return `Změna stavu${by(entry)}`;
    }
    case 'assignee_changed': {
      if (p.to === null && !str(p.type)) return `Zrušeno přiřazení ticketu${by(entry)}`;
      if (str(p.type) === 'ai') return `Ticket přiřazen AI agentovi${by(entry)}`;
      const target = str(p.targetUserName);
      return target ? `Ticket přiřazen na ${target}${by(entry)}` : `Ticket přiřazen operátorovi${by(entry)}`;
    }
    case 'urgent_changed':
      return p.urgent === true ? `Označeno jako urgentní${by(entry)}` : `Zrušena urgentnost${by(entry)}`;
    case 'visibility_changed': {
      const to = str(p.to);
      return to ? `Změna viditelnosti na „${to}“${by(entry)}` : `Změna viditelnosti${by(entry)}`;
    }
    case 'rondo_user_linked': {
      const linked = p.linkedRondoUserId;
      return linked ? `Spárováno s Rondo uživatelem #${String(linked)}${by(entry)}` : `Zrušeno párování s Rondo uživatelem${by(entry)}`;
    }
    case 'message_archived':
      return `Zpráva archivována${by(entry)}`;
    case 'ticket_archived':
      return `Ticket archivován${by(entry)}`;
    default:
      return `${entry.action}${by(entry)}`;
  }
}
